// SalonStaffManagement.jsx - Staff count, roster, leave, and closure config.
// Writes the same salons/{salonId}.profile.* fields and staffLeaves/salonClosures
// subcollections that the mobile app (UlloorStyles@e7a5f9b) uses, so both apps share one
// source of truth for booking capacity. Config only — no slot/booking-capacity logic is
// added here. Performance tracking (targets, incentives, reports) built on this roster
// is a future addition.
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import StaffCountByDayModal from '../../components/StaffCountByDayModal';
import StaffLeaveModal from '../../components/StaffLeaveModal';
import SalonClosureModal from '../../components/SalonClosureModal';
import ConfirmModal from '../../components/ConfirmModal';
import {
  formatDateLocal,
  makeStaffId,
  MAX_STAFF_PER_BUCKET,
  getUpcomingStaffLeaves,
  saveStaffLeaves,
  getStaffLeaveForDate,
  getUpcomingSalonClosures,
  saveSalonClosures,
  countConfirmedBookingsOnDates,
  updateStaffProfileConfig,
  synthesizeStaffList,
} from '../../services/staffConfigService';
import { getAvailableStaffCountForDate, getStaffCapability } from '../../utils/staffCapacityEngine';
import './SalonStaffManagement.css';

const SERVICE_FOR_OPTIONS = [
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
  { value: 'both', label: 'Both' },
];

function SalonStaffManagement() {
  const { salonData, refreshSalonData } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const servicesFor = salonData?.profile?.servicesFor || 'Unisex';
  const isUnisex = servicesFor === 'Unisex';

  const [staffList, setStaffList] = useState([]);
  const [menStaffList, setMenStaffList] = useState([]);
  const [womenStaffList, setWomenStaffList] = useState([]);

  const [applyToAllDays, setApplyToAllDays] = useState(true);
  const [staffCountByDay, setStaffCountByDay] = useState({});
  const [staffDaySchedule, setStaffDaySchedule] = useState({});
  const [showStaffCountModal, setShowStaffCountModal] = useState(false);

  const [genderStaffCount, setGenderStaffCount] = useState({ men: {}, women: {} });
  const [applyMenToAllDays, setApplyMenToAllDays] = useState(true);
  const [applyWomenToAllDays, setApplyWomenToAllDays] = useState(true);
  const [menStaffCountByDay, setMenStaffCountByDay] = useState({});
  const [womenStaffCountByDay, setWomenStaffCountByDay] = useState({});
  const [menStaffDaySchedule, setMenStaffDaySchedule] = useState({});
  const [womenStaffDaySchedule, setWomenStaffDaySchedule] = useState({});
  const [showMenStaffCountModal, setShowMenStaffCountModal] = useState(false);
  const [showWomenStaffCountModal, setShowWomenStaffCountModal] = useState(false);

  const [pendingRemoval, setPendingRemoval] = useState(null);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [staffLeaves, setStaffLeaves] = useState({});
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [savingLeaves, setSavingLeaves] = useState(false);

  const [showClosureModal, setShowClosureModal] = useState(false);
  const [salonClosures, setSalonClosures] = useState({});
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [savingClosures, setSavingClosures] = useState(false);
  const [checkingClosureImpact, setCheckingClosureImpact] = useState(false);
  // Set once the salon clicks Save in SalonClosureModal and the affected-booking count for the
  // newly-added dates comes back — holds the pending write until the salon confirms it.
  const [pendingClosureSave, setPendingClosureSave] = useState(null);

  const [todayLeaveDoc, setTodayLeaveDoc] = useState(null);

  // AuthContext's salonData does not live-refresh after a write (getSalonData only runs at
  // login), so anything derived from it — like the capacity preview below — would keep showing
  // pre-save numbers after a successful Save. This tracks the flattened dot-path fields from the
  // last successful save (e.g. 'profile.staffList') and overlays them on top of salonData.profile
  // wherever that derived data is read, so the preview reflects what was just saved. Reset
  // whenever salonData itself changes, since a fresh fetch supersedes any local override.
  const [profileOverrides, setProfileOverrides] = useState({});

  // Re-derive every piece of local state from salonData.profile. AuthContext's salonData does
  // not live-refresh after a write (getSalonData only runs at login), so after a successful
  // Save we update this component's state directly rather than relying on this effect re-firing.
  const resetFromProfile = () => {
    const profile = salonData?.profile || {};
    setProfileOverrides({});

    const resolvedStaffList = synthesizeStaffList(profile.staffList, profile.staffCount ?? 1);
    const resolvedMenStaffList = synthesizeStaffList(profile.menStaffList, profile.genderStaffCount?.men?.count ?? 1, 'men');
    const resolvedWomenStaffList = synthesizeStaffList(profile.womenStaffList, profile.genderStaffCount?.women?.count ?? 1, 'women');

    setStaffList(resolvedStaffList);
    setMenStaffList(resolvedMenStaffList);
    setWomenStaffList(resolvedWomenStaffList);
    setPendingRemoval(null);
    setStaffDaySchedule(profile.staffCountData?.staffSchedule || {});
    setMenStaffDaySchedule(profile.genderStaffCount?.men?.daySchedule || {});
    setWomenStaffDaySchedule(profile.genderStaffCount?.women?.daySchedule || {});

    const staffCountData = profile.staffCountData;
    if (staffCountData) {
      setApplyToAllDays(staffCountData.type === 'all_days');
      setStaffCountByDay(staffCountData.type === 'by_day' ? (staffCountData.counts || {}) : {});
    } else {
      setApplyToAllDays(true);
      setStaffCountByDay({});
    }

    const existingGenderData = profile.genderStaffCount;

    if (existingGenderData) {
      setGenderStaffCount(existingGenderData);
      if (existingGenderData.men?.byDay && Object.keys(existingGenderData.men.byDay).length > 0) {
        setApplyMenToAllDays(false);
        setMenStaffCountByDay(existingGenderData.men.byDay);
      } else {
        setApplyMenToAllDays(true);
        setMenStaffCountByDay({});
      }
      if (existingGenderData.women?.byDay && Object.keys(existingGenderData.women.byDay).length > 0) {
        setApplyWomenToAllDays(false);
        setWomenStaffCountByDay(existingGenderData.women.byDay);
      } else {
        setApplyWomenToAllDays(true);
        setWomenStaffCountByDay({});
      }
    } else {
      setGenderStaffCount({ men: {}, women: {} });
      setApplyMenToAllDays(true);
      setApplyWomenToAllDays(true);
      setMenStaffCountByDay({});
      setWomenStaffCountByDay({});
    }
  };

  useEffect(() => {
    if (salonData) resetFromProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonData]);

  useEffect(() => {
    if (!salonData?.salonId) return;
    setLoadingLeaves(true);
    getUpcomingStaffLeaves(salonData.salonId).then(setStaffLeaves).finally(() => setLoadingLeaves(false));
    setLoadingClosures(true);
    getUpcomingSalonClosures(salonData.salonId).then(setSalonClosures).finally(() => setLoadingClosures(false));
    getStaffLeaveForDate(salonData.salonId, formatDateLocal(new Date())).then(setTodayLeaveDoc).catch(() => setTodayLeaveDoc(null));
  }, [salonData?.salonId]);

  // Adding is immediate and unconfirmed (low-risk, one row at a time). Removing always goes
  // through a confirm prompt and, once confirmed, takes effect immediately in local state —
  // there's no separate "select N to remove then Save" step anymore.
  const addStaffRow = (setList, defaultServiceFor) => {
    setList((prev) => {
      if (prev.length >= MAX_STAFF_PER_BUCKET) return prev;
      return [
        ...prev,
        { id: makeStaffId(), name: `Staff ${prev.length + 1}`, ...(defaultServiceFor ? { serviceFor: defaultServiceFor } : {}) },
      ];
    });
  };

  const removeStaffRow = (setList, id, name) => {
    setPendingRemoval({ setList, id, name });
  };

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    pendingRemoval.setList((prev) => prev.filter((s) => s.id !== pendingRemoval.id));
    setPendingRemoval(null);
  };

  const handleCancel = () => {
    resetFromProfile();
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    setMessage({ type: '', text: '' });

    let staffCountData;
    if (applyToAllDays) {
      staffCountData = { type: 'all_days', count: staffList.length };
    } else {
      if (!staffCountByDay || Object.keys(staffCountByDay).length === 0) {
        setMessage({ type: 'error', text: 'Please set a custom staff count for each day.' });
        return;
      }
      staffCountData = { type: 'by_day', counts: staffCountByDay };
      if (Object.keys(staffDaySchedule).length > 0) {
        staffCountData.staffSchedule = staffDaySchedule;
      }
    }

    const updatedStaffCount = applyToAllDays ? staffList.length : (Object.values(staffCountByDay)[0] ?? 1);

    const profileUpdates = {
      'profile.staffCount': updatedStaffCount,
      'profile.staffCountData': staffCountData,
      'profile.staffList': staffList,
    };

    if (isUnisex) {
      const menCount = menStaffList.length;
      const womenCount = womenStaffList.length;

      const genderStaffData = {
        men: { count: menCount },
        women: { count: womenCount },
      };

      if (!applyMenToAllDays && Object.keys(menStaffCountByDay).length > 0) {
        genderStaffData.men.byDay = menStaffCountByDay;
        if (Object.keys(menStaffDaySchedule).length > 0) genderStaffData.men.daySchedule = menStaffDaySchedule;
      }
      if (!applyWomenToAllDays && Object.keys(womenStaffCountByDay).length > 0) {
        genderStaffData.women.byDay = womenStaffCountByDay;
        if (Object.keys(womenStaffDaySchedule).length > 0) genderStaffData.women.daySchedule = womenStaffDaySchedule;
      }

      profileUpdates['profile.genderStaffCount'] = genderStaffData;

      // BACKWARD COMPATIBILITY: staffCount is the MIN across buckets so old app builds that
      // don't understand gender-split counts never overbook.
      let minMen = menCount;
      let minWomen = womenCount;
      if (!applyMenToAllDays && Object.keys(menStaffCountByDay).length > 0) {
        minMen = Math.min(...Object.values(menStaffCountByDay));
      }
      if (!applyWomenToAllDays && Object.keys(womenStaffCountByDay).length > 0) {
        minWomen = Math.min(...Object.values(womenStaffCountByDay));
      }
      profileUpdates['profile.staffCount'] = Math.min(minMen, minWomen);

      profileUpdates['profile.menStaffList'] = menStaffList;
      profileUpdates['profile.womenStaffList'] = womenStaffList;
    }

    try {
      setSaving(true);
      await updateStaffProfileConfig(salonData.salonId, profileUpdates);
      setProfileOverrides((prev) => {
        const next = { ...prev };
        Object.entries(profileUpdates).forEach(([key, value]) => {
          next[key.replace(/^profile\./, '')] = value;
        });
        return next;
      });
      // Refresh AuthContext's salonData so other screens (e.g. Dashboard's "add staff" check)
      // see the newly saved staff without requiring a re-login.
      refreshSalonData();
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Staff configuration updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save staff configuration. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStaffLeaves = async (leaves) => {
    if (!salonData?.salonId) return;
    try {
      setSavingLeaves(true);
      const existingDates = Object.keys(staffLeaves);
      const newDates = Object.keys(leaves);
      const datesToDelete = existingDates.filter((d) => !newDates.includes(d));
      await saveStaffLeaves(salonData.salonId, leaves, datesToDelete);
      setStaffLeaves(leaves);
      setShowLeaveModal(false);
      setMessage({ type: 'success', text: 'Staff leaves updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save staff leaves. Please try again.' });
    } finally {
      setSavingLeaves(false);
    }
  };

  // Actually persists the closure days — only called once the salon has seen and confirmed the
  // affected-booking count (or there was nothing to confirm, i.e. no newly-added dates).
  const persistClosures = async (closuresMap, datesToDelete) => {
    try {
      setSavingClosures(true);
      const { rescheduleRequestsSent } = await saveSalonClosures(salonData.salonId, closuresMap, datesToDelete, salonClosures);
      setSalonClosures(closuresMap);
      setShowClosureModal(false);
      const text = rescheduleRequestsSent > 0
        ? `Closure days updated. Reschedule requests sent for ${rescheduleRequestsSent} affected booking(s).`
        : 'Salon closures updated successfully!';
      setMessage({ type: 'success', text });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save salon closures. Please try again.' });
    } finally {
      setSavingClosures(false);
    }
  };

  // Called from SalonClosureModal's Save button. Newly-added closure dates may have confirmed
  // bookings on them, so look up how many before writing anything — the salon confirms the exact
  // impact (reschedule requests sent, or none) via pendingClosureSave below.
  const handleSaveClosures = async (closuresMap) => {
    if (!salonData?.salonId) return;
    const existingDates = Object.keys(salonClosures);
    const newDates = Object.keys(closuresMap);
    const datesToDelete = existingDates.filter((d) => !newDates.includes(d));
    const addedDates = newDates.filter((d) => !existingDates.includes(d));

    if (addedDates.length === 0) {
      // Only removing closures (or no change) — nothing to confirm.
      await persistClosures(closuresMap, datesToDelete);
      return;
    }

    try {
      setCheckingClosureImpact(true);
      const bookingCount = await countConfirmedBookingsOnDates(salonData.salonId, addedDates);
      setPendingClosureSave({ closuresMap, datesToDelete, addedDates, bookingCount });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to check affected bookings. Please try again.' });
    } finally {
      setCheckingClosureImpact(false);
    }
  };

  const handleConfirmClosureSave = async () => {
    if (!pendingClosureSave) return;
    const { closuresMap, datesToDelete } = pendingClosureSave;
    setPendingClosureSave(null);
    await persistClosures(closuresMap, datesToDelete);
  };

  const renderStaffRow = (list, setList, entry, { withServiceFor = false, withRemove = false, disabled = false } = {}) => (
    <div key={entry.id} className="staff-roster-row">
      <div className="staff-roster-row-header">
        <input
          type="text"
          className="modal-input staff-name-input"
          value={entry.name}
          onChange={(e) => setList((prev) => prev.map((s) => (s.id === entry.id ? { ...s, name: e.target.value } : s)))}
          placeholder="Staff name"
          disabled={disabled}
        />
        {withRemove && !disabled && (
          <button type="button" className="remove-staff-btn" onClick={() => removeStaffRow(setList, entry.id, entry.name)}>
            🗑️
          </button>
        )}
      </div>
      {withServiceFor && (
        <div className="service-for-row">
          {SERVICE_FOR_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt.value}
              disabled={disabled}
              className={`service-for-btn ${entry.serviceFor === opt.value ? 'service-for-btn-active' : ''}`}
              onClick={() => setList((prev) => prev.map((s) => (s.id === entry.id ? { ...s, serviceFor: opt.value } : s)))}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const buildBucketConfig = (bucket) => {
    if (bucket === 'men') {
      return {
        list: menStaffList, setList: setMenStaffList,
        applyToAllDays: applyMenToAllDays, setApplyToAllDays: setApplyMenToAllDays,
        countByDay: menStaffCountByDay, setCountByDay: setMenStaffCountByDay,
        daySchedule: menStaffDaySchedule, setDaySchedule: setMenStaffDaySchedule,
        showByDayModal: showMenStaffCountModal, setShowByDayModal: setShowMenStaffCountModal,
        defaultServiceFor: 'men',
        withServiceFor: true,
        label: "men's staff",
      };
    }
    if (bucket === 'women') {
      return {
        list: womenStaffList, setList: setWomenStaffList,
        applyToAllDays: applyWomenToAllDays, setApplyToAllDays: setApplyWomenToAllDays,
        countByDay: womenStaffCountByDay, setCountByDay: setWomenStaffCountByDay,
        daySchedule: womenStaffDaySchedule, setDaySchedule: setWomenStaffDaySchedule,
        showByDayModal: showWomenStaffCountModal, setShowByDayModal: setShowWomenStaffCountModal,
        defaultServiceFor: 'women',
        withServiceFor: true,
        label: "women's staff",
      };
    }
    return {
      list: staffList, setList: setStaffList,
      applyToAllDays, setApplyToAllDays,
      countByDay: staffCountByDay, setCountByDay: setStaffCountByDay,
      daySchedule: staffDaySchedule, setDaySchedule: setStaffDaySchedule,
      showByDayModal: showStaffCountModal, setShowByDayModal: setShowStaffCountModal,
      defaultServiceFor: null,
      withServiceFor: false,
      label: 'staff',
    };
  };

  const renderCountSection = (bucket) => {
    const cfg = buildBucketConfig(bucket);
    const atCap = cfg.list.length >= MAX_STAFF_PER_BUCKET;

    return (
      <div className="count-section">
        <div className="count-mode-toggle">
          <label className="radio-label">
            <input type="radio" checked={cfg.applyToAllDays} onChange={() => cfg.setApplyToAllDays(true)} disabled={!isEditing} />
            Same every day
          </label>
          <label className="radio-label">
            <input type="radio" checked={!cfg.applyToAllDays} onChange={() => cfg.setApplyToAllDays(false)} disabled={!isEditing} />
            Custom by day
          </label>
        </div>

        {cfg.applyToAllDays ? (
          <div className="fixed-count-row">
            <span className="modal-label">Total staff</span>
            <span className="total-staff-value">{cfg.list.length}</span>
          </div>
        ) : (
          <button type="button" className="edit-section-btn" onClick={() => cfg.setShowByDayModal(true)} disabled={!isEditing}>
            🗓️ Set Custom Staff Count by Day
          </button>
        )}

        <div className="staff-roster">
          <div className="staff-roster-header">
            <span className="modal-label">Roster</span>
            {isEditing && (
              <button
                type="button"
                className="add-staff-btn-small"
                onClick={() => addStaffRow(cfg.setList, cfg.defaultServiceFor)}
                disabled={atCap}
              >
                ➕ Add staff
              </button>
            )}
          </div>
          {cfg.list.map((entry) => renderStaffRow(cfg.list, cfg.setList, entry, {
            withServiceFor: cfg.withServiceFor,
            withRemove: isEditing,
            disabled: !isEditing,
          }))}
        </div>

        <StaffCountByDayModal
          isOpen={cfg.showByDayModal}
          onClose={() => cfg.setShowByDayModal(false)}
          onSave={(counts, schedule) => {
            cfg.setCountByDay(counts);
            if (schedule) cfg.setDaySchedule(schedule);
          }}
          initialStaffCounts={cfg.countByDay}
          roster={cfg.list}
          initialStaffSchedule={cfg.daySchedule}
        />
      </div>
    );
  };

  const effectiveSalonData = salonData
    ? { ...salonData, profile: { ...(salonData.profile || {}), ...profileOverrides } }
    : null;

  const capacityPreview = effectiveSalonData?.profile
    ? (isUnisex
      ? { type: 'unisex', capability: getStaffCapability(effectiveSalonData, new Date(), todayLeaveDoc) }
      : { type: 'single', count: getAvailableStaffCountForDate(effectiveSalonData, new Date(), null, todayLeaveDoc) })
    : null;

  const upcomingLeavesList = Object.entries(staffLeaves).sort(([a], [b]) => a.localeCompare(b)).slice(0, 5);
  const upcomingClosuresList = Object.keys(salonClosures).sort().slice(0, 5);

  const leaveModalSalonData = {
    staffCount: staffList.length,
    genderStaffCount: {
      ...genderStaffCount,
      men: { ...genderStaffCount.men, count: menStaffList.length },
      women: { ...genderStaffCount.women, count: womenStaffList.length },
    },
    staffList,
    menStaffList,
    womenStaffList,
  };

  return (
    <div className="salon-staff-management">
      <div className="staffs-header">
        <div>
          <h1>Staff Management</h1>
          <p className="subtitle">
            Manage staff count, roster, leave, and closures — shared with the mobile app as a single source of truth.
          </p>
        </div>
      </div>

      {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

      {!salonData ? (
        <p className="loading-text">Loading...</p>
      ) : (
        <>
          {capacityPreview && (
            <div className="capacity-preview-card">
              <span className="capacity-preview-label">Today&apos;s capacity:</span>
              {capacityPreview.type === 'unisex' ? (
                <span className="capacity-preview-value">
                  👨 {capacityPreview.capability.menOnly} men-only · 👩 {capacityPreview.capability.womenOnly} women-only · 🤝 {capacityPreview.capability.both} both
                </span>
              ) : (
                <span className="capacity-preview-value">{capacityPreview.count} staff</span>
              )}
            </div>
          )}

          <div className="section-card">
            <div className="section-card-header">
              <h2>Staff &amp; Schedule</h2>
              {!isEditing ? (
                <button type="button" className="edit-section-btn" onClick={() => setIsEditing(true)}>✏️ Edit</button>
              ) : (
                <div className="section-actions">
                  <button type="button" className="cancel-modal-btn" onClick={handleCancel}>Cancel</button>
                  <button type="button" className="save-modal-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {isUnisex ? (
              <div className="gender-columns">
                <div className="gender-column">
                  <h3>👨 Men&apos;s Staff</h3>
                  {renderCountSection('men')}
                </div>
                <div className="gender-column">
                  <h3>👩 Women&apos;s Staff</h3>
                  {renderCountSection('women')}
                </div>
              </div>
            ) : (
              renderCountSection('all')
            )}
          </div>

          <div className="section-card">
            <div className="section-card-header">
              <h2>Staff Leaves</h2>
              <button type="button" className="edit-section-btn" onClick={() => setShowLeaveModal(true)}>📅 Manage Staff Leaves</button>
            </div>
            {loadingLeaves ? (
              <p className="loading-text">Loading...</p>
            ) : upcomingLeavesList.length === 0 ? (
              <p className="empty-hint">No upcoming leaves scheduled.</p>
            ) : (
              <ul className="preview-list">
                {upcomingLeavesList.map(([date, data]) => (
                  <li key={date}>
                    <strong>{date}</strong> — {isUnisex ? `👨${data.menLeaveCount ?? 0} 👩${data.womenLeaveCount ?? 0}` : `${data.leaveCount ?? 0} on leave`}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="section-card">
            <div className="section-card-header">
              <h2>Salon Closures</h2>
              <button type="button" className="edit-section-btn" onClick={() => setShowClosureModal(true)}>🚫 Manage Closures</button>
            </div>
            {loadingClosures ? (
              <p className="loading-text">Loading...</p>
            ) : upcomingClosuresList.length === 0 ? (
              <p className="empty-hint">No upcoming closures scheduled.</p>
            ) : (
              <ul className="preview-list">
                {upcomingClosuresList.map((date) => <li key={date}>{date}</li>)}
              </ul>
            )}
          </div>
        </>
      )}

      <StaffLeaveModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onSave={handleSaveStaffLeaves}
        salonData={leaveModalSalonData}
        existingLeaves={staffLeaves}
        isUnisex={isUnisex}
        isSaving={savingLeaves}
      />

      <SalonClosureModal
        isOpen={showClosureModal}
        onClose={() => setShowClosureModal(false)}
        onSave={handleSaveClosures}
        existingClosures={salonClosures}
        isSaving={savingClosures || checkingClosureImpact}
      />

      <ConfirmModal
        isOpen={!!pendingRemoval}
        title="Remove staff member?"
        message={`Remove ${pendingRemoval?.name || 'this staff member'} from the roster? This can't be undone.`}
        confirmLabel="Remove"
        danger
        onConfirm={confirmRemoval}
        onCancel={() => setPendingRemoval(null)}
      />

      <ConfirmModal
        isOpen={!!pendingClosureSave}
        title="Confirm salon closure"
        message={
          pendingClosureSave?.bookingCount > 0
            ? `Marking ${pendingClosureSave.addedDates.length} day(s) as closed will send a reschedule request to ${pendingClosureSave.bookingCount} confirmed booking(s) already scheduled on ${pendingClosureSave.addedDates.length === 1 ? 'that date' : 'those dates'}. The customer keeps their slot until they pick a new time. Continue?`
            : `No existing bookings on the selected date(s) — this will simply mark ${pendingClosureSave?.addedDates?.length === 1 ? 'it' : 'them'} closed for new bookings. Continue?`
        }
        confirmLabel="Yes, Continue"
        cancelLabel="Go Back"
        danger={pendingClosureSave?.bookingCount > 0}
        onConfirm={handleConfirmClosureSave}
        onCancel={() => setPendingClosureSave(null)}
      />
    </div>
  );
}

export default SalonStaffManagement;
